import React from "react";
import { Link } from "react-router-dom";
import { Search, UserCheck, Send, CheckCircle2, ChevronRight, Phone } from "lucide-react";

export default function HowItWorks() {
  const steps = [
    {
      num: "01",
      title: "Digital Lead Generation",
      description: "We generate targeted homeowner inquiries using online advertising and search marketing. Homeowners searching for local contractors find our professional web platforms and request direct project quotes.",
      icon: Search,
    },
    {
      num: "02",
      title: "Inquiry Review & Qualification",
      description: "Our dedicated systems and staff review and qualify each inquiry. We verify key details including geographic location, project trade scope, ownership status, and immediate intent before clearing the lead.",
      icon: UserCheck,
    },
    {
      num: "03",
      title: "Immediate Exclusive Delivery",
      description: "Validated high-intent leads are delivered directly to contractors. Information is sent in real-time via email or SMS, ensuring you have the highest possible chance of connecting instantly.",
      icon: Send,
    },
    {
      num: "04",
      title: "Direct Contractor Connection",
      description: "Contractors contact homeowners directly to schedule site inspections and finalize project bids. Because the lead is exclusive, you can build rapport immediately with zero competing pressure.",
      icon: CheckCircle2,
    },
  ];

  return (
    <div className="bg-white" data-testid="how-it-works-page">
      
      {/* Page Header */}
      <section className="bg-slate-900 text-white py-16 md:py-24 text-center space-y-4">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 max-w-3xl space-y-3">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-400">Our Workflow</span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
            How Precision Home Leads Generates Qualified Growth
          </h1>
          <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
            A transparent, highly refined four-step operational process designed for optimal lead quality and maximum contractor conversion rates.
          </p>
        </div>
      </section>

      {/* 4 Steps Section */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          
          <div className="max-w-4xl mx-auto space-y-16 md:space-y-24">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div 
                  key={step.num} 
                  className={`flex flex-col md:flex-row items-start gap-8 md:gap-12 lg:gap-16 ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
                  data-testid={`workflow-step-${step.num}`}
                >
                  {/* Left Icon and number indicator */}
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-4xl sm:text-5xl font-extrabold text-slate-200 tracking-tight leading-none">
                      {step.num}
                    </span>
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded bg-slate-900 border border-slate-800 text-white flex items-center justify-center shadow-inner">
                      <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-500" />
                    </div>
                  </div>

                  {/* Right Content */}
                  <div className="space-y-4 max-w-xl">
                    <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">
                      {step.title}
                    </h3>
                    <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* Underwriting Trust Assurance */}
      <section className="bg-slate-50 border-t border-slate-200/80 py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h3 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              A Direct Commitment to Legitimate Lead Inbound Capture
            </h3>
            <p className="text-sm text-slate-500 leading-relaxed">
              We never buy scraping databases, use artificial bots, or run misleading social clickbait. Every lead is captured voluntarily by the homeowner directly submitting the digital request on our verified web domains. This guarantees compliance with credit card merchants, TCPA, and federal consumer privacy guidelines.
            </p>
            <div className="pt-2">
              <Link 
                to="/contact" 
                className="inline-flex items-center justify-center px-5 py-2.5 rounded bg-slate-900 hover:bg-slate-800 text-white font-medium text-sm transition-colors"
                data-testid="how-it-works-contact-btn"
              >
                Get Started Today
                <ChevronRight className="ml-1.5 h-4 w-4 text-emerald-500" />
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
